/* global chrome */
import React, { useState, useEffect, useRef, useCallback } from 'react';

import {
  Avatar,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Skeleton,
} from '@mui/material';

import { Clear } from '@mui/icons-material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

import SignIn from 'components/home/signIn';
import Extension from 'components/extension';
import Welcome from 'components/welcome';

import LibraryShell from 'components/library/LibraryShell';
import LibraryProfileBar from 'components/library/LibraryProfileBar';
import {
  CARD_VIEW_ZOOM_MAX,
  CARD_VIEW_ZOOM_MIN,
  CARD_VIEW_ZOOM_STEP,
} from 'constants/cardImageZoom';
import LibraryToolbar from 'components/library/LibraryToolbar';
import ActivityGallery from 'components/library/ActivityGallery';
import LibraryContentLoading from 'components/library/LibraryContentLoading';
import LoadingScreen from 'components/loadingScreen';
import { useLibraryNav } from 'components/library/useLibraryNav';

import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { usePersonStore } from 'stores';
import { useBoardStore } from 'canvas';
import socket from 'socket';

import 'components/library/index.css';

const zoomIconButtonCardSx = theme => ({
  borderRadius: '50%',
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: theme.shadows[2],
  '&:hover': {
    bgcolor: 'background.paper',
    boxShadow: theme.shadows[6],
  },
});

const Activity = () => {
  const { root, appType, extensionInstalled } = useSelector(state => state.api);
  const [inverseZoom, setInverseZoom] = useState(1);
  const [isDialogLoading, setIsDialogLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [dataType, setDataType] = useState(null);
  const [datas, setDatas] = useState([]);

  const personStore = usePersonStore();
  const {
    loadPerson,
    loadBoard,
    prependCanvasActivity,
    followPerson,
    unfollowPerson,
    addUserHashtag,
  } = personStore;
  const [creatingCanvas, setCreatingCanvas] = useState(false);
  const [createCanvasError, setCreateCanvasError] = useState(null);

  const user = useSelector(state => state.user);
  const { person, activities, type, hasMore, loadedTime, userCanvasMapping, keyword } =
    personStore;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const scrollRef = useRef(null);
  const paperRefs = useRef([]);

  const libraryNav = useLibraryNav();
  const {
    filteredActivities,
    activeSection,
    searchQuery,
    boardName,
    libraryViewMode,
    sectionTitle,
    personName,
    navigateAll,
    navigateBoard,
    navigateCreate,
    setViewMode,
  } = libraryNav;

  const handleCreateCanvas = useCallback(
    async (rawName, rawDescription) => {
      const name = String(rawName || '')
        .trim()
        .replace(/^#+/, '')
        .trim();
      if (!name || name.toLowerCase() === 'no_hashtag') {
        setCreateCanvasError('Enter a hashtag name for your canvas.');
        return;
      }
      const description = String(rawDescription || '').trim().slice(0, 500);
      setCreatingCanvas(true);
      setCreateCanvasError(null);
      const newHashtag = {
        name,
        color: '#6366f1',
        loweredName: name.toLowerCase(),
      };
      try {
        const res = await fetch(`${root}/api/user/tag`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newHashtag),
          credentials: 'include',
        });
        const body = await res.json().catch(() => null);
        if (!res.ok) {
          setCreateCanvasError(body?.error || body?.message || 'Could not create canvas.');
          return;
        }
        addUserHashtag(newHashtag);
        // Show an empty canvas immediately; loadBoard will refresh in the background.
        const ownerName = personName || person?.username || user?.username;
        const boardStore = useBoardStore.getState();
        if (ownerName) {
          boardStore.prepareEmptyBoard(ownerName, name);
        }
        if (description) {
          // Persist the board goal before navigation so Do-next can call the model.
          // Must run after prepareEmptyBoard seeds hashtag, and before description is
          // already set locally (saveDescription no-ops when unchanged).
          await boardStore.saveDescription(description);
        }
        navigateBoard(name, { canvas: true });
      } catch (err) {
        console.error('create canvas failed', err);
        setCreateCanvasError('Could not create canvas. Try again.');
      } finally {
        setCreatingCanvas(false);
      }
    },
    [root, addUserHashtag, navigateBoard, personName, person, user]
  );

  const profileUsername = params.person_name
    ? decodeURIComponent(params.person_name)
    : null;

  const isOwnerProfile = Boolean(
    user &&
      (person
        ? String(person._id) === String(user._id)
        : profileUsername &&
          user.username &&
          user.username.toLowerCase() === profileUsername.toLowerCase())
  );

  const ownerUsernameTooltip = person?.username
    ? `This is @${person.username} — the username your friends use to message you.`
    : '';

  const handleLoadMore = useCallback(() => {
    dispatch({ type: 'MORE_ACTIVITY' });
  }, [dispatch]);

  const handleLoadFollowers = () => {
    if (type === 'NO_ACCESS') return;
    setIsDialogLoading(true);
    setIsOpen(true);
    setDataType('Followers');
    fetch(`${root}/api/followers?person_id=${person._id}`, { credentials: 'include' })
      .then(res => res.json())
      .then(json => {
        setIsDialogLoading(false);
        setDatas(json.followers);
      })
      .catch(err => console.log(err));
  };

  const handleLoadFollowings = () => {
    if (type === 'NO_ACCESS') return;
    setIsDialogLoading(true);
    setIsOpen(true);
    setDataType('Followings');
    fetch(`${root}/api/followings?person_id=${person._id}`, { credentials: 'include' })
      .then(res => res.json())
      .then(json => {
        setIsDialogLoading(false);
        setDatas(json.followings);
      })
      .catch(err => console.log(err));
  };

  const handleFollowerClick = p => {
    setIsOpen(false);
    setDatas([]);
    navigate(`/${p.username}`);
  };

  const handleFollowingClick = p => {
    setIsOpen(false);
    setDatas([]);
    navigate(`/${p.username}`);
  };

  const handleRemoveFollower = p => {
    fetch(`${root}/auth/relation?type=REMOVE_FOLLOWER&person_id=${p._id}`, {
      method: 'post',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(() => {
        setDatas(datas.filter(data => data.person._id !== p._id));
      })
      .catch(err => console.log(err));
  };

  const handleUnfollow = p => {
    fetch(`${root}/auth/relation?type=UNFOLLOW&person_id=${p._id}`, {
      method: 'post',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(() => {
        setDatas(datas.filter(data => data.person._id !== p._id));
      })
      .catch(err => console.log(err));
  };

  const zoomGrid = (ref, isZoomOut) => {
    if (!ref?.current) return;
    const currentZoom = parseFloat(ref.current.style.zoom || 1);
    const newZoom = isZoomOut ? currentZoom - 0.25 : currentZoom + 0.25;
    if (newZoom < CARD_VIEW_ZOOM_MIN || newZoom > CARD_VIEW_ZOOM_MAX) return;
    ref.current.style.zoom = `${newZoom}`;
    ref.current.style.width = `${window.innerWidth / newZoom}px`;
    setInverseZoom(1 / newZoom);
  };

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'ArrowRight') zoomGrid(scrollRef, false);
      else if (event.key === 'ArrowLeft') zoomGrid(scrollRef, true);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (appType !== 'WEB') return;
    if (location.pathname.startsWith('/extension/')) return;
    if (location.pathname.endsWith('/canvas')) return;
    if (params.keyword) {
      loadBoard(params.person_name, params.keyword);
    } else {
      loadPerson(params.person_name);
    }
  }, [appType, location.pathname, params.person_name, params.keyword, loadBoard, loadPerson]);

  useEffect(() => {
    if (!user?._id || !params.keyword) return undefined;
    const onCanvasActivityAdded = payload => {
      if (!payload?.activity) return;
      prependCanvasActivity(payload.activity, payload.canvasName);
    };
    socket.on('CANVAS_ACTIVITY_ADDED', onCanvasActivityAdded);
    if (!socket.connected) socket.connect();
    return () => {
      socket.off('CANVAS_ACTIVITY_ADDED', onCanvasActivityAdded);
    };
  }, [user?._id, params.keyword, prependCanvasActivity]);

  useEffect(() => {
    const handleScroll = () => {
      const totalPageHeight = document.body.scrollHeight;
      const scrollPoint = window.scrollY + window.innerHeight;
      if (scrollPoint >= totalPageHeight && hasMore && !boardName) {
        handleLoadMore();
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activities, hasMore, boardName, handleLoadMore]);

  if (personStore.loading && !person) {
    return <LoadingScreen fullScreen={false} message="Loading your memory" />;
  }

  if (!person) return null;

  if (!user) {
    return (
      <div>
        <div align="center" style={{ marginTop: '100px' }}>
          <SignIn />
        </div>
        <div style={{ textAlign: 'center', backgroundColor: '#fafafa', marginTop: '50px' }}>
          <div style={{ display: 'flex', paddingTop: '50px', justifyContent: 'center' }}>
            <Avatar alt="" src={person.profileImageUrl} />
            <Typography style={{ margin: '20px' }}>@{person.username}</Typography>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <Button color="secondary" sx={{ borderRadius: '20px', backgroundColor: 'white' }}>
              {person.followingCount} following
            </Button>
            <Button color="secondary" sx={{ borderRadius: '20px', backgroundColor: 'white' }}>
              {person.followerCount} followers
            </Button>
          </div>
          <div style={{ margin: '20px' }}>
            <p>{person.description}</p>
          </div>
          <div style={{ margin: '20px' }}>
            <p>Click your pins to interact with them</p>
          </div>
        </div>
      </div>
    );
  }

  const isFollowed = person.followStatus?.[0]?.status === 'FOLLOW';
  const isFriend = person.followStatus?.[0]?.status === 'FRIEND';
  const isOwner = isOwnerProfile;

  const contentType = keyword ? 'KEYWORD_FEED' : 'FEED';

  const ZoomIcons = () => (
    <div className="library-zoom-sidebar-icons">
      <IconButton
        size="large"
        sx={zoomIconButtonCardSx}
        onClick={() => {
          if (!scrollRef.current) return;
          const currentZoom = parseFloat(scrollRef.current.style.zoom || 1);
          const newZoom = currentZoom + CARD_VIEW_ZOOM_STEP;
          if (newZoom > CARD_VIEW_ZOOM_MAX) return;
          scrollRef.current.style.zoom = `${newZoom}`;
          scrollRef.current.style.width = `${window.innerWidth / newZoom}px`;
          setInverseZoom(1 / newZoom);
        }}
      >
        <ZoomInIcon />
      </IconButton>
      <IconButton
        size="large"
        sx={zoomIconButtonCardSx}
        onClick={() => {
          if (!scrollRef.current) return;
          const currentZoom = parseFloat(scrollRef.current.style.zoom || 1);
          const newZoom = currentZoom - CARD_VIEW_ZOOM_STEP;
          if (newZoom < CARD_VIEW_ZOOM_MIN) return;
          scrollRef.current.style.zoom = `${newZoom}`;
          scrollRef.current.style.width = `${window.innerWidth / newZoom}px`;
          setInverseZoom(1 / newZoom);
        }}
      >
        <ZoomOutIcon />
      </IconButton>
    </div>
  );

  // First login (no pins yet): show create-canvas instead of an empty All items grid.
  const hasNoPins = !personStore.loading && (activities?.length || 0) === 0;
  const showCreateScreen =
    isOwner &&
    !boardName &&
    !searchQuery &&
    (activeSection === 'create' || (activeSection === 'all' && hasNoPins));

  const mainContent = personStore.loading ? (
    <LibraryContentLoading />
  ) : showCreateScreen ? (
    <Welcome
      mode="create-canvas"
      onCreateCanvas={handleCreateCanvas}
      creating={creatingCanvas}
      error={createCanvasError}
    />
  ) : (
    <ActivityGallery
      activities={filteredActivities}
      scrollRef={scrollRef}
      paperRefs={paperRefs}
      inverseZoom={inverseZoom}
      loadedTime={loadedTime}
      contentType={contentType}
    />
  );

  return (
    <>
      {!extensionInstalled ? <Extension /> : null}
      <LibraryShell
        flushContent={showCreateScreen}
        profileBar={
          <LibraryProfileBar
            person={person}
            isOwner={isOwner}
            isFollowed={isFollowed}
            isFriend={isFriend}
            ownerUsernameTooltip={ownerUsernameTooltip}
            onLoadFollowings={handleLoadFollowings}
            onLoadFollowers={handleLoadFollowers}
            onFollowToggle={async () => {
              if (!user) return dispatch({ type: 'OPEN_SIGN_$' });
              try {
                if (!isFollowed && !isFriend) {
                  await followPerson(person._id);
                } else {
                  await unfollowPerson(person._id);
                }
                await loadPerson(person.username, undefined, { force: true });
              } catch (err) {
                console.error('follow/unfollow failed', err);
              }
            }}
          />
        }
        sidebarProps={{
          activeSection,
          boardName,
          searchQuery,
          person,
          isOwner,
          userCanvasMapping,
          onNavigateAll: navigateAll,
          onNavigateBoard: name => navigateBoard(name),
          onNavigateCreate: navigateCreate,
        }}
        toolbar={
          <LibraryToolbar
            title={showCreateScreen ? 'New board' : sectionTitle}
            viewMode={libraryViewMode}
            boardName={boardName}
            pinCount={filteredActivities.length}
            onViewModeChange={setViewMode}
            personName={params.person_name}
            isOwner={isOwner}
          />
        }
      >
        {mainContent}
        {libraryViewMode === 'gallery' && !personStore.loading && !showCreateScreen ? (
          <aside className="library-zoom-sidebar" aria-label="Zoom controls">
            <ZoomIcons />
          </aside>
        ) : null}
      </LibraryShell>

      <Dialog onClose={() => setIsOpen(false)} open={isOpen}>
        <DialogTitle>{dataType}</DialogTitle>
        {isDialogLoading ? (
          <Skeleton />
        ) : (
          <List sx={{ pt: 0 }}>
            {datas.map(data => {
              const listPerson = data.person;
              if (dataType === 'Followers') {
                return (
                  <ListItem button key={listPerson._id} onClick={() => handleFollowerClick(listPerson)}>
                    <ListItemAvatar>
                      <Avatar src={listPerson.profileImageUrl} />
                    </ListItemAvatar>
                    <ListItemText primary={listPerson.username} />
                    {isOwner ? (
                      <ListItemSecondaryAction style={{ paddingLeft: '50px' }}>
                        <IconButton size="small" onClick={() => handleRemoveFollower(listPerson)}>
                          <Clear fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    ) : null}
                  </ListItem>
                );
              }
              return (
                <ListItem button key={listPerson._id} onClick={() => handleFollowingClick(listPerson)}>
                  <ListItemAvatar>
                    <Avatar src={listPerson.profileImageUrl} />
                  </ListItemAvatar>
                  <ListItemText primary={listPerson.username} />
                  {isOwner ? (
                    <ListItemSecondaryAction style={{ paddingLeft: '50px' }}>
                      <IconButton size="small" onClick={() => handleUnfollow(listPerson)}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  ) : null}
                </ListItem>
              );
            })}
          </List>
        )}
      </Dialog>
    </>
  );
};

export default Activity;
